import type { ErrorEvent } from '@sentry/nextjs';

import { getEventErrorMessage } from '../filters/createErrorFilter';
import { CHUNK_LOAD_ERROR_PATTERNS } from '../filters/patterns/nextjs';

/**
 * 청크 로드 실패(ChunkLoadError/dynamic import/CSS chunk) 이벤트를 단일 그룹으로 수렴시킨다.
 *
 * 청크 에러 메시지는 chunk id/hash 를 포함해 빌드마다 달라지므로,
 * 그대로 두면 같은 근본 원인(EKS 카나리 buildId 스큐 등, ZEP-591)이
 * 빌드마다 별도 이슈로 흩어져 추이 관측이 불가능하다.
 * 고정 fingerprint 로 단일 이슈에 수렴시키고, 태그를 남겨
 * 대시보드/알림에서 필터 가능하게 한다.
 *
 * 참고: beforeSend 의 ignore 필터 "통과 이후"에만 호출되므로,
 * CHUNK_LOAD_ERROR_PATTERNS 를 ignorePatterns 로 쓰는 앱(대부분)에서는
 * 애초에 이벤트가 드롭되어 이 fingerprint 가 적용될 일이 없다.
 */
function isChunkLoadError(event: ErrorEvent): boolean {
  // ignore 필터와 동일한 추출기(getEventErrorMessage)를 사용한다.
  // 첫 번째 exception.value 만 보면 연쇄 예외(cause 체인)의 후속 항목이나
  // type 에만 'ChunkLoadError' 가 기록된 이벤트를 놓쳐, 업로드는 되면서
  // fingerprint/태그가 빠지는 반쪽 관측이 된다. 전체 values(type: value 결합)
  // + message 를 합쳐 판정해 필터 단계와 매칭 표면을 일치시킨다.
  const message = getEventErrorMessage(event) ?? '';
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern =>
    typeof pattern === 'string'
      ? message.includes(pattern)
      : pattern.test(message),
  );
}

/**
 * 청크 자동복구(ZEP-655)가 event.tags['chunk_recovery'] 로 남기는 단계값.
 * 이 값이 있으면 fingerprint 에 붙여 단계별로 이슈를 분리한다 —
 * 특히 'failed'(복구 실패)는 카나리 램프업 중단/롤백 게이트 지표라
 * 'chunk-load-error' 뭉치에 섞이면 알람을 걸 수 없다.
 */
const CHUNK_RECOVERY_STAGES = new Set(['cache-bust', 'failed']);

export function applyChunkLoadFingerprint(event: ErrorEvent): void {
  // beforeSend에서 throw 발생 시 Sentry SDK가 해당 이벤트를 드롭하므로,
  // fingerprint 적용 실패가 원본 에러 보고를 막지 않도록 안전망을 둔다.
  try {
    if (!isChunkLoadError(event)) return;

    // 복구 단계 태그가 있으면 fingerprint 를 단계별로 쪼갠다(cache-bust/failed).
    // 태그가 없는 순수 청크 에러(초기 로드 등)는 기존대로 단일 그룹에 수렴.
    const recovery = event.tags?.['chunk_recovery'];
    event.fingerprint =
      typeof recovery === 'string' && CHUNK_RECOVERY_STAGES.has(recovery)
        ? ['chunk-load-error', recovery]
        : ['chunk-load-error'];

    if (!event.tags) event.tags = {};
    event.tags['chunk_load_error'] = 'true';
  } catch {
    // ignore: fingerprint 그룹핑은 부가 기능, 실패해도 원본 이벤트는 보존
  }
}
